import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyleProps } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import {
  Dialog,
  DialogActionClose,
  DialogActionPrimary,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTrigger,
} from '@sola/ark-core/molecules/dialog/dialog.tsx';
import {
  RadioGroup,
  RadioGroupProps,
} from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { usePaymentMethodRemove } from '@sola/my-sdk/payment-methods/hooks.ts';
import { PaymentTypeEnum } from '@sola/schema/dotcom/proxy-api/paymentMethods.ts';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.ts';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { CommitmentAvatarGroup } from '../../molecules/commitment-avatar-group/commitment-avatar-group.tsx';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import {
  createPaymentMethodSuccessAlert,
  createSupportAlert,
  removePaymentMethodFailureAlert,
  removePaymentMethodPartialSuccessAlert,
} from '../../utils/alerts.tsx';
import { getErrorTranslationKeys } from '../../utils/errors.ts';

const PaymentMethodLabel = ({
  paymentMethod,
  ignoreNickname = false,
  isBold = false,
}: {
  paymentMethod: GraphQLSupporterPaymentMethod;
  ignoreNickname?: boolean;
  isBold?: boolean;
}) => {
  const { accountNickname, paymentType, creditCard, ach } = paymentMethod;
  const showNickname = !ignoreNickname && accountNickname;
  const label = (
    <IntlBlock
      i18nKey={
        showNickname
          ? 'my:payments.methodLabels.withNickname'
          : 'my:payments.methodLabels.default'
      }
      values={{
        nickname: accountNickname,
        type:
          paymentType === PaymentTypeEnum.CreditCard
            ? creditCard?.cardType
            : 'Checking', // TODO: can we use a translated tag for this?
        lastFour:
          paymentType === PaymentTypeEnum.CreditCard
            ? creditCard?.lastFour
            : ach?.lastFourOfAccountNumber,
      }}
    />
  );

  if (isBold && !showNickname) {
    return <strong>{label}</strong>;
  }

  return label;
};

const PaymentMethodSupportingText = ({
  paymentMethod,
}: {
  paymentMethod: GraphQLSupporterPaymentMethod;
}) => {
  return (
    <IntlBlock
      i18nKey="my:deletePaymentMethod.removeMethodDialog.supportingTextRecurring"
      components={{
        PaymentLabel: (
          <PaymentMethodLabel paymentMethod={paymentMethod} isBold />
        ),
      }}
    />
  );
};

const mapPaymentMethodsToRadioGroupItem = (
  paymentMethods?: GraphQLSupporterPaymentMethod[],
): RadioGroupProps['items'] => {
  if (!paymentMethods?.length) {
    return undefined;
  }

  return paymentMethods
    .map((paymentMethod) => {
      if (
        !paymentMethod.id ||
        (!paymentMethod.ach && !paymentMethod.creditCard)
      ) {
        return null;
      }

      return {
        value: paymentMethod.id,
        element: (
          <span className="flex items-center gap-comfy-xs">
            <PaymentMethodLabel paymentMethod={paymentMethod} ignoreNickname />
            {paymentMethod.accountNickname && (
              <Badge label={paymentMethod.accountNickname} size="sm" />
            )}
          </span>
        ),
      };
    })
    .filter((item) => !!item);
};

export type PaymentMethodRemoveDialogProps = Omit<DialogProps, 'modal'> & {
  openTrigger: React.ReactElement<ButtonStyleProps>;
  recurringCommitments: GraphQLCommitment[];
  selectedPaymentMethod: GraphQLSupporterPaymentMethod;
  alternatePaymentMethods: GraphQLSupporterPaymentMethod[];
};

export const PaymentMethodRemoveDialog: React.FC<
  PaymentMethodRemoveDialogProps
> = ({
  openTrigger,
  selectedPaymentMethod,
  recurringCommitments,
  alternatePaymentMethods,
}) => {
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [newPaymentMethodId, setNewPaymentMethodId] = useState<
    GraphQLSupporterPaymentMethod['id'] | null
  >(null);

  const { mutateAsync, isPending } = usePaymentMethodRemove(
    selectedPaymentMethod?.id || undefined,
  );
  const { addAlerts, clearAlerts } = useLocalAlerts();

  const { t } = useTranslation('my');

  const handleRemove = async () => {
    if (!selectedPaymentMethod.id) {
      return;
    }

    clearAlerts();
    try {
      const { success, isPartialSuccess } = await mutateAsync(
        newPaymentMethodId || undefined,
      );

      if (success && !isPartialSuccess) {
        addAlerts(
          createPaymentMethodSuccessAlert(
            selectedPaymentMethod,
            'deletePaymentMethod.success.action',
          ),
        );
      }
      if (!success && isPartialSuccess) {
        addAlerts(removePaymentMethodPartialSuccessAlert());
      }
      if (!success && !isPartialSuccess) {
        addAlerts(removePaymentMethodFailureAlert());
      }
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodDelete,
        errors,
      );
      addAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }

    // TODO: Make sure dialog can close when form submits
    setIsRemoveDialogOpen(false);
  };

  return (
    <Dialog open={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
      <DialogTrigger>{openTrigger}</DialogTrigger>
      <DialogContent
        size="md"
        icon="info-circle"
        purpose="negative"
        title={t('deletePaymentMethod.removeMethodDialog.title')}
        supportingText={
          recurringCommitments.length ? (
            <PaymentMethodSupportingText
              paymentMethod={selectedPaymentMethod}
            />
          ) : (
            t('deletePaymentMethod.removeMethodDialog.supportingText')
          )
        }
      >
        {!!recurringCommitments.length && (
          <>
            <div className="body-neutral-potent">
              <RadioGroup
                name="payment-method-select"
                loop={false}
                value={newPaymentMethodId}
                onChange={(e) => {
                  setNewPaymentMethodId(e.target.value);
                }}
                items={mapPaymentMethodsToRadioGroupItem(
                  alternatePaymentMethods,
                )}
              />
            </div>
            <Divider purpose="neutral" className="my-comfy-xl" />
            <div className="flex flex-col gap-comfy-sm">
              <p className="body-neutral-mild">
                <IntlBlock i18nKey="my:deletePaymentMethod.removeMethodDialog.newPaymentMethodNote" />
              </p>
              <CommitmentAvatarGroup
                commitments={recurringCommitments}
                layout="stacked"
                tooltipMessage={t('payments.methodCommitmentsTooltip', {
                  count: recurringCommitments.length,
                })}
                usePortal={false}
              />
            </div>
          </>
        )}
        <DialogActions>
          {recurringCommitments.length ? (
            <>
              <DialogActionClose>
                {t('deletePaymentMethod.removeMethodDialog.cancel')}
              </DialogActionClose>
              <DialogActionPrimary
                purpose="negative"
                isBusy={isPending}
                disabled={!newPaymentMethodId}
                onClick={handleRemove}
              >
                {t('deletePaymentMethod.removeMethodDialog.saveAndRemove')}
              </DialogActionPrimary>
            </>
          ) : (
            <>
              <DialogActionClose>
                {t('deletePaymentMethod.removeMethodDialog.cancel')}
              </DialogActionClose>
              <DialogActionPrimary
                purpose="negative"
                isBusy={isPending}
                onClick={handleRemove}
              >
                {t('deletePaymentMethod.removeMethodDialog.remove')}
              </DialogActionPrimary>
            </>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
